import { useEffect, useMemo } from "react";
import { useFieldArray, Controller } from "react-hook-form";
import { SwivuCombo } from "../../../SwivuCombo.jsx";
import { INCENTIVE_FILTER_OPERATORS, INCENTIVE_FILTER_OPERATOR_VALUES, INCENTIVE_FILTER_BOOLEAN_OPERATORS } from "@/other/constants/IncentiveKeys.jsx";
import { getIncentiveFilterOptions } from "@/other/utils/incentiveUtils.jsx";
import { Button } from "@/modern/components/ui/button";

const DEFAULT_FILTER = {
  field: "",
  operator: INCENTIVE_FILTER_OPERATORS.IS,
  value: "",
  booleanOperator: null
};

export const OutcomeIncentiveConditionalFilters = ({
  control,
  name,
  fieldOptions,
  entityType,
  project,
  addButtonLabel,
  clearable,
  errors,
  isRequired = false,
  emptyMessage,
  disabled,
  onDeleteFilter,
  duplicateErrorMessage,
  createOptionOnUnmatched
}) => {
  const { fields, append, remove, update, replace } = useFieldArray({
    control,
    name
  });

  // Memoized value options function to prevent expensive recalculation on every render
  const memoizedValueOptions = useMemo(() => {
    const cache = new Map();

    return (field) => {
      if (!field) return [];

      if (!cache.has(field)) {
        cache.set(field, getIncentiveFilterOptions(field, entityType, project));
      }

      return cache.get(field);
    };
  }, [entityType, project]);

  // Initialize with default values if empty
  useEffect(() => {
    const currentValue = control._formValues[name];
    // For required filters, always ensure at least one filter exists
    if (isRequired && (!currentValue || currentValue.length === 0)) {
      replace([DEFAULT_FILTER]);
    }
  }, [control, name, isRequired, replace]);

  const addFilter = () => {
    const newFilter = {
      ...DEFAULT_FILTER,
      booleanOperator: fields.length === 0 ? null : INCENTIVE_FILTER_BOOLEAN_OPERATORS.AND
    };

    append(newFilter, { shouldValidate: false });
  };

  const removeFilter = (index) => {
    onDeleteFilter?.(fields[index]);
    remove(index);

    // Update booleanOperator for remaining filters
    if (fields.length > 1) {
      const updatedFields = fields.filter((_, i) => i !== index);
      updatedFields.forEach((_, i) => {
        if (i === 0) {
          update(i, { ...updatedFields[i], booleanOperator: null });
        }
      });
    }
  };

  const handleFieldChange = (index, field, value) => {
    const currentFilter = fields[index];
    const updatedFilter = { ...currentFilter, [field]: value };

    // If field is being changed, clear the value
    if (field === "field") {
      updatedFilter.value = "";
    }

    update(index, updatedFilter);
  };

  // Show error message for required filters
  const showRequiredError = isRequired && errors && fields.length === 0;

  const getFieldValueNotFoundError = (field, value) => {
    if (!value) return null;
    const fieldOptions = memoizedValueOptions(field);
    const fieldOption = fieldOptions.find((option) => option.value === value);
    return fieldOption ? null : "The option you previously selected has been removed";
  }

  return (
    <>
      {duplicateErrorMessage && (
        <div className="mt-1 text-swivu-sm text-destructive mb-swivu-vertical">
          {duplicateErrorMessage}
        </div>
      )}
      {fields.length === 0 && !isRequired ? (
        <div className="text-swivu-sm text-muted-foreground italic mb-swivu-vertical">
          {emptyMessage || "No filters applied"}
        </div>
      ) : (
        fields.map((field, index) => (
          <div key={field.id}>
            {field.booleanOperator && (
              <OperatorToggle
                value={field.booleanOperator}
                onChange={(value) => handleFieldChange(index, "booleanOperator", value)}
              />
            )}
            <div className="flex items-start gap-swivu-horizontal mb-swivu-vertical">
              <Controller
                control={control}
                name={`${name}.${index}.field`}
                render={({ field: { value, onChange } }) => (
                  <SwivuCombo
                    value={value}
                    onChange={(newValue) => {
                      onChange(newValue);
                      handleFieldChange(index, "field", newValue);
                    }}
                    options={fieldOptions}
                    placeholder="Select Field"
                    className="flex-1"
                    error={errors?.[index]?.field?.message}
                    disabled={disabled}
                  />
                )}
              />
              <Controller
                control={control}
                name={`${name}.${index}.operator`}
                render={({ field: { value, onChange } }) => (
                  <SwivuCombo
                    value={value}
                    onChange={(newValue) => {
                      onChange(newValue);
                      handleFieldChange(index, "operator", newValue);
                    }}
                    options={Object.entries(INCENTIVE_FILTER_OPERATOR_VALUES).map(([key, value]) => ({ label: value, value: key }))}
                    placeholder={INCENTIVE_FILTER_OPERATOR_VALUES[INCENTIVE_FILTER_OPERATORS.IS]}
                    className="w-32"
                    error={errors?.[index]?.operator?.message}
                    disabled={disabled}
                  />
                )}
              />
              <Controller
                control={control}
                name={`${name}.${index}.value`}
                render={({ field: { value, onChange } }) => (
                  <SwivuCombo
                    disabled={!fields[index]?.field || disabled}
                    value={value}
                    onChange={(newValue) => {
                      onChange(newValue);
                      handleFieldChange(index, "value", newValue);
                    }}
                    options={memoizedValueOptions(fields[index]?.field)}
                    placeholder="Select Option"
                    className="flex-1"
                    error={errors?.[index]?.value?.message || getFieldValueNotFoundError(fields[index]?.field, value)}
                    createOnUnmatched={createOptionOnUnmatched}
                  />
                )}
              />
              {(clearable || fields.length > 1) && (
                <button
                  type="button"
                  onClick={() => removeFilter(index)}
                  className="size-swivu-icon text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-swivu-base transition-colors bg-transparent border-0 font-swivu-modern cursor-pointer flex items-center justify-center my-auto"
                  disabled={disabled}
                >
                  <i className="fas fa-times text-[1.2rem]" />
                </button>
              )}
            </div>
          </div>
        ))
      )}

      {/* Show required error outside the map */}
      {showRequiredError && (
        <div className="mt-1 text-swivu-sm text-destructive mb-swivu-vertical">
          At least one filter is required
        </div>
      )}

      <Button
        type="button"
        onClick={addFilter}
        variant={"ghost"}
        className={"border-input border"}
        disabled={disabled}
      >
        <i className="fas fa-plus text-swivu-icon" />
        {addButtonLabel}
      </Button>
    </>
  );
};

const OperatorToggle = ({ value, onChange, disabled }) => {
  return (
    <div className="flex items-center justify-center mb-swivu-vertical">
      <div className="flex bg-card rounded-swivu-base p-1">
        <button
          type="button"
          onClick={() => onChange(INCENTIVE_FILTER_BOOLEAN_OPERATORS.AND)}
          className={`leading-none px-3 py-2 text-swivu-sm font-medium rounded-swivu-base transition-all font-swivu-modern cursor-pointer disabled:opacity-50 disabled:pointer-events-none ${
            value === INCENTIVE_FILTER_BOOLEAN_OPERATORS.AND
              ? "bg-secondary text-secondary-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
          disabled={disabled}
        >
          AND
        </button>
        <button
          type="button"
          onClick={() => onChange(INCENTIVE_FILTER_BOOLEAN_OPERATORS.OR)}
          className={`leading-none px-3 py-2 text-swivu-sm font-medium rounded-swivu-base transition-all font-swivu-modern cursor-pointer disabled:opacity-50 disabled:pointer-events-none ${
            value === INCENTIVE_FILTER_BOOLEAN_OPERATORS.OR
              ? "bg-secondary text-secondary-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
          disabled={disabled}
        >
          OR
        </button>
      </div>
    </div>
  );
};